import React, { useState, useEffect, useRef, useMemo } from 'react'
import {
  makeStyles,
  AppBar,
  Tabs,
  Tab,
  Card,
  Divider,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
} from '@material-ui/core'
import { Edit as EditIcon, ArrowBack as ArrowBackIcon, Delete as DeleteIcon } from '@material-ui/icons'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import TabPanel from '../../components/TabPanel/TabPanel'
import cmsAdminPageStyles from './cmsAdminPage.styles'
import COLORS from '../../utils/constants/colors'
import StageFilter from './StageFilter/StageFilter'
import GroupFilter from './GroupFilter/GroupFilter'
import StagesTab from './StagesTab/StagesTab'
import GroupsTab from './GroupsTab/GroupsTab'
import TeamsTab from './TeamsTab/TeamsTab'
import GamedaysTab from './GamedaysTab/GamedaysTab'
import GamesTab from './GamesTab/GamesTab'
import useUrlSearchParams from '../../hooks/useUrlSearchParams'
import useHistoryPush from '../../hooks/useHistoryPush'
import useGetLocalStorage from '../../hooks/useGetLocalStorage'
import localStorageIds from '../../utils/constants/localStorageIds'
import { EVENT_TYPE_FORMS } from '../../utils/constants/eventTypeForms'
import { setLocalStorageValue, removeLocalStorageValue } from '../../utils/helpers/localStorageHelperMethods'
import AddSeasonModal from '../../components/AddSeasonModal/AddSeasonModal'
import getStages from '../../Apis/stages/getStages'
import getGamesByContext from '../../Apis/event/getGamesByContext'
import attachGamesToStages from '../../utils/helpers/attachGamesToStages'
import deleteSeason from '../../Apis/season/deleteSeason'
import QueryKeys from '../../utils/constants/queryKeys'
import CreateEventDrawer from '../Calendar/CreateEventDrawer'
import { TeamCalendarProvider, useTeamCalendarContext } from '../../context/useTeamCalendarContext'
import LiteModeBadge from '../../components/LiteModeBadge/LiteModeBadge'

const useStyles = makeStyles(cmsAdminPageStyles)

const CmsAdminPageCompetitionContent = () => {
  const localStorageValue = useGetLocalStorage()
  const { [localStorageIds.SELECTED_SEASON]: selectedSeason } = localStorageValue || {}
  const { id: seasonId } = selectedSeason || {}
  const [tab, setTab] = useState(0)
  const [selectedStage, setSelectedStage] = useState(null)
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [selectedGameday, setSelectedGameday] = useState(null)
  const [isEditSeasonModalOpen, setIsEditSeasonModalOpen] = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [gameToDelete, setGameToDelete] = useState(null)
  const [deleteSeasonConfirmOpen, setDeleteSeasonConfirmOpen] = useState(false)

  // Use context for CreateEventDrawer state
  const { setCreateEventDrawerOpen, setGamedayId, setClickedEventId, setDates, setSelectedEventType, eventTypes, deleteEvent, createEventDrawerOpen } = useTeamCalendarContext()

  const classes = useStyles()
  const queryClient = useQueryClient()
  const { org: orgId } = useUrlSearchParams(['org']) || {}
  const { redirect } = useHistoryPush()
  const storedStage = localStorageValue?.[localStorageIds.SELECTED_STAGE] || null
  const storedGroup = localStorageValue?.[localStorageIds.SELECTED_GROUP] || null
  const initializedFromStorage = useRef(false)

  // Fetch stages to keep selectedGroup in sync with latest data
  const { data: rawStages, isLoading: isLoadingStages } = useQuery(
    [QueryKeys.GET_STAGES, orgId, seasonId],
    () => getStages({ orgId, seasonId }),
    {
      enabled: Boolean(orgId && seasonId),
    }
  )

  // Games come from the dedicated by-context endpoint (no longer embedded in /stages) and
  // are merged back onto the tree by gamedayId so the tabs keep reading gameday.games.
  const { data: games = [] } = useQuery(
    [QueryKeys.GET_GAMES_BY_CONTEXT, orgId, seasonId],
    () => getGamesByContext({ seasonId }),
    { enabled: Boolean(orgId && seasonId) }
  )
  const stages = useMemo(() => attachGamesToStages(rawStages || [], games), [rawStages, games])
  const invalidateGames = () => queryClient.invalidateQueries([QueryKeys.GET_GAMES_BY_CONTEXT, orgId, seasonId])

  // Refresh games when the create/edit game drawer closes (a game may have been saved).
  const prevDrawerOpen = useRef(false)
  useEffect(() => {
    if (prevDrawerOpen.current && !createEventDrawerOpen) invalidateGames()
    prevDrawerOpen.current = createEventDrawerOpen
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createEventDrawerOpen])

  // Delete season mutation
  const deleteSeasonMutation = useMutation(
    () => deleteSeason(orgId, seasonId),
    {
      onSuccess: () => {
        localStorage.removeItem(localStorageIds.SELECTED_SEASON)
        queryClient.invalidateQueries([QueryKeys.GET_SEASONS])
        setDeleteSeasonConfirmOpen(false)

        redirect('/calendar')
      },
    }
  )

  // Initialize stage and group from localStorage on mount
  useEffect(() => {
    if (!stages || stages.length === 0 || initializedFromStorage.current) return

    if (storedStage) {
      const stageToSet = stages.find((s) => s.id === storedStage.id)
      if (stageToSet) {
        setSelectedStage(stageToSet)

        if (storedGroup) {
          const groupToSet = stageToSet.groups?.find((g) => g.id === storedGroup.id)
          if (groupToSet) {
            setSelectedGroup(groupToSet)
          }
        }
      }
    }

    initializedFromStorage.current = true
  }, [stages, storedStage, storedGroup])

  // Sync selectedStage with latest stages data
  useEffect(() => {
    if (!selectedStage || !stages) return

    const updatedStage = stages.find(s => s.id === selectedStage.id)
    if (updatedStage) {
      setSelectedStage(updatedStage)
    } else {
      // Stage was deleted
      setSelectedStage(null)
      setSelectedGroup(null)
    }
  }, [stages])

  // Sync selectedGroup with latest stages data
  useEffect(() => {
    if (!selectedGroup || !stages) return

    // Find the updated group from the latest stages data
    for (const stage of stages) {
      const updatedGroup = stage.groups?.find(g => g.id === selectedGroup.id)
      if (updatedGroup) {
        setSelectedGroup(updatedGroup)
        return
      }
    }

    // If group not found, it might have been deleted
    setSelectedGroup(null)
    setSelectedGameday(null)
  }, [stages])

  // Sync selectedGameday with latest stages data
  useEffect(() => {
    if (!selectedGameday || !stages) return

    // Find the updated gameday from the latest stages data
    for (const stage of stages) {
      for (const group of stage.groups || []) {
        const updatedGameday = group.gamedays?.find(gd => gd.id === selectedGameday.id)
        if (updatedGameday) {
          setSelectedGameday(updatedGameday)
          return
        }
      }
    }

    // If gameday not found, it might have been deleted
    setSelectedGameday(null)
  }, [stages])

  const handleChange = (event, newValue) => {
    setTab(newValue)
  }

  const handleStageChange = (stage) => {
    setSelectedStage(stage)

    if (initializedFromStorage.current && selectedStage?.id !== stage?.id) {
      setSelectedGroup(null)
      setSelectedGameday(null)
      setTab(0)

      if (stage) {
        setLocalStorageValue([{ id: localStorageIds.SELECTED_STAGE, value: stage }])
        removeLocalStorageValue([localStorageIds.SELECTED_GROUP])
      } else {
        removeLocalStorageValue([localStorageIds.SELECTED_STAGE, localStorageIds.SELECTED_GROUP])
      }
    }
  }

  const handleGroupChange = (group) => {
    setSelectedGroup(group)

    if (initializedFromStorage.current && selectedGroup?.id !== group?.id) {
      setSelectedGameday(null)
      setTab(0)

      if (group) {
        setLocalStorageValue([{ id: localStorageIds.SELECTED_GROUP, value: group }])
      } else {
        removeLocalStorageValue([localStorageIds.SELECTED_GROUP])
      }
    }
  }

  const handleGamedayChange = (gameday) => {
    setSelectedGameday(gameday)
    if (gameday) {
      setTab(0) // Switch to Games tab when gameday is selected
    }
  }

  const handleBack = () => {
    if (selectedGameday) {
      setSelectedGameday(null)
      setTab(0)
    } else if (selectedGroup) {
      setSelectedGroup(null)
      setTab(0)
      removeLocalStorageValue([localStorageIds.SELECTED_GROUP])
    } else if (selectedStage) {
      setSelectedStage(null)
      setTab(0)
      removeLocalStorageValue([localStorageIds.SELECTED_STAGE, localStorageIds.SELECTED_GROUP])
    }
  }

  const handleNewGameClick = () => {
    // Set event type to GAME
    const gameEventType = eventTypes?.data?.find((type) => type.code === EVENT_TYPE_FORMS.GAME)
    if (gameEventType) {
      setSelectedEventType(gameEventType)
    }

    // Set dates to enable event fetching - use a wide range to include all games
    const now = new Date()
    const start = new Date(now.getFullYear() - 1, 0, 1) // Start of last year
    const end = new Date(now.getFullYear() + 1, 11, 31) // End of next year
    setDates({ start, end })

    setGamedayId(selectedGameday?.id || null)
    setClickedEventId('')
    setCreateEventDrawerOpen(true)
  }

  const handleEditGameClick = (gameId) => {
    // Set event type to GAME
    const gameEventType = eventTypes?.data?.find((type) => type.code === EVENT_TYPE_FORMS.GAME)
    if (gameEventType) {
      setSelectedEventType(gameEventType)
    }

    // Set dates to enable event fetching - use a wide range to include all games
    const now = new Date()
    const start = new Date(now.getFullYear() - 1, 0, 1) // Start of last year
    const end = new Date(now.getFullYear() + 1, 11, 31) // End of next year
    setDates({ start, end })

    setGamedayId(selectedGameday?.id || null)
    setClickedEventId(gameId)
    setCreateEventDrawerOpen(true)
  }

  const handleDeleteGameClick = (game) => {
    setGameToDelete(game)
    setDeleteConfirmOpen(true)
  }

  const handleConfirmDelete = () => {
    if (gameToDelete) {
      // Pass both eventTypeCode and eventId as the API expects
      deleteEvent.mutate(
        { eventTypeCode: 'game', eventId: gameToDelete.id },
        { onSuccess: invalidateGames }
      )
      setDeleteConfirmOpen(false)
      setGameToDelete(null)
    }
  }

  const handleOpenEditSeasonModal = () => {
    setIsEditSeasonModalOpen(true)
  }

  const handleCloseEditSeasonModal = () => {
    setIsEditSeasonModalOpen(false)
  }

  const handleDeleteSeasonClick = () => {
    setDeleteSeasonConfirmOpen(true)
  }

  const handleConfirmDeleteSeason = () => {
    deleteSeasonMutation.mutate()
  }

  const a11yProps = (index) => {
    return {
      id: `cms-tab-${index}`,
      'aria-controls': `cms-tabpanel-${index}`,
      className: classes.indicator,
    }
  }

  const getDisplayTitle = () => {
    if (selectedGameday && selectedGroup && selectedStage) {
      return `${selectedSeason?.name} - ${selectedStage.name} - ${selectedGroup.name} - ${selectedGameday.name}`
    }
    if (selectedGroup && selectedStage) {
      return `${selectedSeason?.name} - ${selectedStage.name} - ${selectedGroup.name}`
    }
    if (selectedStage) {
      return `${selectedSeason?.name} - ${selectedStage.name}`
    }
    return selectedSeason?.name || 'Competition Management'
  }

  // Define tabs dynamically based on current state
  const getTabs = () => {
    if (!selectedStage) {
      return [
        {
          label: 'Stages',
          component: (
            <StagesTab
              orgId={orgId}
              seasonId={seasonId}
              stages={stages}
              isLoading={isLoadingStages}
              onStageClick={handleStageChange}
              onGroupClick={handleGroupChange}
            />
          ),
        },
      ]
    }

    if (selectedGameday) {
      return [
        {
          label: 'Games',
          component: (
            <GamesTab
              selectedGameday={selectedGameday}
              onNewGameClick={handleNewGameClick}
              onEditGameClick={handleEditGameClick}
              onDeleteGameClick={handleDeleteGameClick}
            />
          ),
        },
      ]
    }

    if (selectedGroup) {
      return [
        {
          label: 'Gamedays',
          component: (
            <GamedaysTab
              orgId={orgId}
              seasonId={seasonId}
              selectedStage={selectedStage}
              selectedGroup={selectedGroup}
              isLoading={isLoadingStages}
              onGamedayClick={handleGamedayChange}
            />
          ),
        },
        {
          label: 'Teams',
          component: (
            <TeamsTab
              orgId={orgId}
              seasonId={seasonId}
              selectedGroup={selectedGroup}
              gender={selectedSeason?.gender}
              ageRestriction={selectedSeason?.ageRestriction}
              isLoading={isLoadingStages}
            />
          ),
        },
      ]
    }

    return [
      {
        label: 'Groups',
        component: (
          <GroupsTab
            orgId={orgId}
            seasonId={seasonId}
            selectedStage={selectedStage}
            isLoading={isLoadingStages}
            onGroupClick={handleGroupChange}
          />
        ),
      },
    ]
  }

  const tabs = getTabs()

  return (
    <>
      {/* Filters Section - Persistent across tabs */}
      <div className={classes.filtersContainer}>
        <div className={classes.titleSection}>
          {(selectedStage || selectedGroup || selectedGameday) && (
            <IconButton
              onClick={handleBack}
              size='small'
              style={{ marginRight: '8px', color: COLORS.orange }}
              aria-label='go back'
            >
              <ArrowBackIcon />
            </IconButton>
          )}
          <Typography
            variant='h5'
            component='div'
            style={{ display: 'flex', alignItems: 'center', gap: '8px', flex: 1, minWidth: 0 }}
          >
            <span
              title={getDisplayTitle()}
              style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}
            >
              {getDisplayTitle()}
            </span>
            <LiteModeBadge statsMode={selectedSeason?.statsMode} />
          </Typography>
          {!selectedStage && !selectedGroup && !selectedGameday && selectedSeason && (
            <>
              <IconButton
                onClick={handleOpenEditSeasonModal}
                size='small'
                style={{ marginLeft: '8px', color: COLORS.orange }}
                aria-label='edit season'
              >
                <EditIcon />
              </IconButton>
              <IconButton
                onClick={handleDeleteSeasonClick}
                size='small'
                style={{ marginLeft: '4px', color: COLORS.purple }}
                aria-label='delete season'
              >
                <DeleteIcon />
              </IconButton>
            </>
          )}
        </div>
        <div className={classes.filtersRight}>
          <StageFilter
            stages={stages}
            selectedStage={selectedStage}
            onStageChange={handleStageChange}
            className={classes.filterItem}
          />
          {selectedStage && (
            <GroupFilter
              selectedStage={selectedStage}
              selectedGroup={selectedGroup}
              onGroupChange={handleGroupChange}
              className={classes.filterItem}
            />
          )}
        </div>
      </div>

      {/* Tabs Section */}
      <Card className={classes.root}>
        <AppBar position="static" color="transparent" elevation={0} className={classes.appbar}>
          <Tabs
            value={tab}
            onChange={handleChange}
            TabIndicatorProps={{ style: { background: COLORS.orange } }}
            textColor="inherit"
          >
            {tabs.map((tabItem, index) => (
              <Tab
                key={index}
                label={tabItem.label}
                {...a11yProps(index)}
                style={{
                  color: tab === index ? COLORS.orange : COLORS.lightGrey,
                }}
              />
            ))}
          </Tabs>
        </AppBar>

        <Divider light orientation="horizontal" />

        {/* Tab Content */}
        {tabs.map((tabItem, index) => (
          <TabPanel key={index} value={tab} index={index}>
            {tabItem.component}
          </TabPanel>
        ))}
      </Card>

      {/* Edit Season Modal */}
      <AddSeasonModal
        isOpen={isEditSeasonModalOpen}
        onClose={handleCloseEditSeasonModal}
        seasonData={selectedSeason}
      />

      {/* Delete Game Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the game &quot;{gameToDelete?.title}&quot;?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color='secondary'
            variant='contained'
            disabled={deleteEvent.isLoading}
          >
            {deleteEvent.isLoading ? (
              <CircularProgress size={24} />
            ) : (
              'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Season Confirmation Dialog */}
      <Dialog
        open={deleteSeasonConfirmOpen}
        onClose={() => setDeleteSeasonConfirmOpen(false)}
      >
        <DialogTitle>Confirm Delete Season</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the season &quot;{selectedSeason?.name}&quot;?
          </Typography>
          <Typography style={{ marginTop: '16px', fontWeight: 'bold' }}>
            Warning: This will permanently delete the season and everything related to it, including all stages, groups, teams, and games.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteSeasonConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDeleteSeason}
            color='secondary'
            variant='contained'
            disabled={deleteSeasonMutation.isLoading}
          >
            {deleteSeasonMutation.isLoading ? (
              <CircularProgress size={24} />
            ) : (
              'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Event Drawer */}
      <CreateEventDrawer />
    </>
  )
}

const CmsAdminPageCompetition = () => {
  const localStorageValue = useGetLocalStorage()
  const { [localStorageIds.SELECTED_SEASON]: selectedSeason } = localStorageValue || {}
  const { id: seasonId } = selectedSeason || {}

  return (
    <TeamCalendarProvider teamsSource="season" seasonId={seasonId} requireSeason>
      <CmsAdminPageCompetitionContent />
    </TeamCalendarProvider>
  )
}

export default CmsAdminPageCompetition
